// Guided feature-request intake. When a requestor picks the "Feature request"
// type, this short AI conversation draws out the context a developer needs
// (problem, today's workaround, desired outcome, example, who/how-often), then
// hands back a structured, editable description. It is non-blocking by design:
// the requestor can skip to plain free text at any point, and if the AI is
// unavailable the component quietly steps aside (onSkip).
//
// Stateless on the wire: each turn posts the running transcript to
// /api/qc/suggest/feature-intake; the server replies ask / done / disabled.

import { useEffect, useRef, useState } from 'react';
import {
  composeFeatureDescription,
  type QcFeatureSynthesis,
  type QcIntakeMessage,
} from '@itms-qc/shared';
import { api } from '../lib/api';
import { useApiToken } from '../auth/useApiToken';
import { primaryButtonStyle, textareaStyle } from './form';

interface Props {
  title: string;
  moduleName?: string | null;
  /** Anything the requestor already typed in the description box — seeds turn 1. */
  initialAnswer?: string;
  /** Accepted synthesis → fill the description field and leave guided mode. */
  onComplete: (description: string) => void;
  /** Opt out (or AI unavailable) → fall back to a plain description textarea. */
  onSkip: () => void;
}

type Phase = 'loading' | 'asking' | 'review';

const aiChipStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  background: '#EEEDFE',
  color: '#3C3489',
  borderRadius: 4,
  padding: '1px 6px',
};

export function FeatureIntakeChat({ title, moduleName, initialAnswer, onComplete, onSkip }: Props) {
  const getToken = useApiToken();

  const [messages, setMessages] = useState<QcIntakeMessage[]>([]);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [phase, setPhase] = useState<Phase>('loading');
  const [draft, setDraft] = useState('');
  const startedRef = useRef(false);

  async function runTurn(history: QcIntakeMessage[]) {
    setPhase('loading');
    try {
      const token = await getToken();
      const res = await api.featureIntake(token, { title, moduleName, history });
      if (res.phase === 'disabled') {
        onSkip();
        return;
      }
      if (res.phase === 'ask') {
        setQuestion(res.question);
        setMessages([...history, { role: 'assistant', content: res.question }]);
        setPhase('asking');
        return;
      }
      // phase === 'done'
      applySynthesis(res.synthesis);
    } catch {
      // Network/unknown trouble — never trap the requestor.
      onSkip();
    }
  }

  function applySynthesis(synthesis: QcFeatureSynthesis) {
    setDraft(composeFeatureDescription(synthesis));
    setPhase('review');
  }

  // Kick off the conversation once. Seed with whatever was already typed.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const seed: QcIntakeMessage[] = initialAnswer && initialAnswer.trim()
      ? [{ role: 'user', content: initialAnswer.trim() }]
      : [];
    void runTurn(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function sendAnswer() {
    const a = answer.trim();
    if (!a) return;
    const next: QcIntakeMessage[] = [...messages, { role: 'user', content: a }];
    setMessages(next);
    setAnswer('');
    void runTurn(next);
  }

  // Visible Q&A so far (assistant questions + the user's answers), in order.
  const transcript = messages.filter((m) => m.role !== 'user' || m.content !== initialAnswer?.trim());

  return (
    <div
      style={{
        border: '1px solid #DCD9F7',
        background: '#FAF9FE',
        borderRadius: 8,
        padding: '14px 16px',
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={aiChipStyle}>✦ AI</span>
          <strong style={{ fontSize: 13, color: 'var(--brand-text, #001C3E)' }}>
            Let’s flesh out your request
          </strong>
        </div>
        <button
          type="button"
          onClick={onSkip}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--brand-text-muted, #8BA4B8)',
            cursor: 'pointer',
            fontSize: 12,
            textDecoration: 'underline',
            padding: 0,
          }}
        >
          Skip — I’ll write it myself
        </button>
      </div>

      {/* Transcript of prior Q&A */}
      {transcript.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: phase === 'review' ? 14 : 10 }}>
          {transcript.map((m, i) => (
            <div key={i} style={{ fontSize: 13 }}>
              {m.role === 'assistant' ? (
                <span style={{ color: 'var(--brand-text, #001C3E)' }}>
                  <span style={{ color: '#3C3489', fontWeight: 600 }}>Q: </span>
                  {m.content}
                </span>
              ) : (
                <span style={{ color: 'var(--brand-text-muted, #5A7186)' }}>
                  <span style={{ fontWeight: 600 }}>You: </span>
                  {m.content}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {phase === 'loading' && (
        <div style={{ fontSize: 13, color: 'var(--brand-text-muted, #8BA4B8)' }}>Thinking…</div>
      )}

      {phase === 'asking' && (
        <div>
          {transcript.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--brand-text, #001C3E)', marginBottom: 8 }}>
              <span style={{ color: '#3C3489', fontWeight: 600 }}>Q: </span>
              {question}
            </div>
          )}
          <textarea
            autoFocus
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                sendAnswer();
              }
            }}
            placeholder="Type your answer… (⌘/Ctrl+Enter to send)"
            style={{ ...textareaStyle, minHeight: 70 }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              type="button"
              onClick={sendAnswer}
              disabled={!answer.trim()}
              style={{ ...primaryButtonStyle, opacity: answer.trim() ? 1 : 0.6 }}
            >
              Send
            </button>
          </div>
        </div>
      )}

      {phase === 'review' && (
        <div>
          <div style={{ fontSize: 13, color: 'var(--brand-text, #001C3E)', marginBottom: 8 }}>
            Here’s a draft of your request — edit anything, then use it.
          </div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            style={{ ...textareaStyle, minHeight: 200, fontSize: 13 }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => onComplete(draft.trim())}
              disabled={!draft.trim()}
              style={{ ...primaryButtonStyle, opacity: draft.trim() ? 1 : 0.6 }}
            >
              Use this description
            </button>
            <span style={{ fontSize: 11, color: 'var(--brand-text-muted, #8BA4B8)' }}>
              You can keep editing it after.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
